// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useExcelImportFilePicker } from '@client/banks/components/shared/excel-import';
import {
  BANK_UPLOAD_ENTITIES_REQUIRING_ACCOUNT_IBAN,
  buildBankUploadAccountIbanSet,
} from '@client/banks/excel/bank-upload-account-iban-set';
import { useParseBankUploadFile } from '@client/banks/excel/use-parse-bank-upload-file';
import { useBankUploadData } from '@client/banks/providers/bank-upload-provider';
import type {
  BankUploadListItemMap,
  BankUploadListSection,
} from '@client/banks/types/bank-list-editor';
import type { BankUploadForm } from '@client/banks/types/create-bank-form';
import {
  downloadBankUploadTemplate,
  type BankUploadTemplateEntity,
} from '@client/banks/utils/download-bank-upload-template';
import { isNull } from '@shared/utils';
import { useStepsData } from '@shared/ui-kit/hooks/use-steps';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type BankUploadExcelImportConfig<TSection extends BankUploadListSection> = {
  entity: BankUploadTemplateEntity;
  section: TSection;
  listStep: string;
  selectItems: (
    formData: BankUploadForm,
  ) => BankUploadListItemMap[TSection][] | undefined;
};

/**
 * Shared Excel import step: download template, parse/validate, show row errors,
 * append valid rows into wizard list state, then navigate to the list for review.
 */
export const useBankUploadExcelImport = <TSection extends BankUploadListSection>({
  entity,
  section,
  listStep,
  selectItems,
}: BankUploadExcelImportConfig<TSection>) => {
  const { navigate } = useStepsData();

  const { i18n } = useTranslation();

  const { formData, saveFormData } = useBankUploadData();

  const filePicker = useExcelImportFilePicker();

  const { isParsing, parseErrors, parseFile, applyFileSizeLimit } =
    useParseBankUploadFile(entity);

  const allowedAccountIbans = useMemo(
    () => buildBankUploadAccountIbanSet(formData.accounts),
    [formData.accounts],
  );

  useEffect(() => {
    applyFileSizeLimit(filePicker.selectedFile);
  }, [filePicker.selectedFile, applyFileSizeLimit]);

  const onBackToList = useCallback(() => {
    navigate(listStep);
  }, [listStep, navigate]);

  const onDownloadTemplate = useCallback(() => {
    void downloadBankUploadTemplate(entity, i18n.language);
  }, [entity, i18n.language]);

  const onImport = useCallback(async () => {
    if (isNull(filePicker.selectedFile)) {
      return;
    }

    if (!applyFileSizeLimit(filePicker.selectedFile)) {
      return;
    }

    const parseOptions = BANK_UPLOAD_ENTITIES_REQUIRING_ACCOUNT_IBAN.has(entity)
      ? { allowedAccountIbans }
      : {};

    const result = await parseFile(filePicker.selectedFile, parseOptions);

    if (!result.ok) {
      return;
    }

    const existing = selectItems(formData) ?? [];

    const nextItems = [
      ...(result.rows as BankUploadListItemMap[TSection][]),
      ...existing,
    ];

    saveFormData(section, nextItems as BankUploadForm[TSection]);

    navigate(listStep);
  }, [
    allowedAccountIbans,
    applyFileSizeLimit,
    entity,
    filePicker.selectedFile,
    formData,
    listStep,
    navigate,
    parseFile,
    saveFormData,
    section,
    selectItems,
  ]);

  return {
    ...filePicker,
    isParsing,
    parseErrors,
    onBackToList,
    onDownloadTemplate,
    onImport,
  };
};
